import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  TextField,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Close, ExpandMore, HelpOutline } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCurrentCanvas,
  selectCurrentHighlights,
  selectCurrentRubricLoadGen,
  selectCurrentRubricSettings,
  selectEditorRubricCriteria,
  selectGradeDimensionWeights,
  selectSelectedCanvasId,
  selectSelectedReadingId,
  selectWorkspaceId,
} from "@store/selectors";
import {
  setExpertCanvas,
  setExpertHighlights,
  setEditorRubricCriteria,
  setGradeDimensionWeight,
  setRubricCategoryMismatch,
  setRubricCompleteness,
  setMatchThreshold,
  setRubricUseRecommended,
} from "@store/slices/workspaceSlice";
import { updateCanvasRubric, updateReadingRubric } from "@services/api";
import {
  DEFAULT_CATEGORY_MISMATCH_PENALTY,
  DEFAULT_COMPLETENESS_PENALTY,
  DEFAULT_MATCH_THRESHOLD,
  MAX_MATCH_THRESHOLD,
  TYPICAL_MATCH_THRESHOLD_RANGE,
} from "@shared/utils/defaultRubricSettings";

const RUBRIC_AUTOSAVE_DELAY_MS = 500;

/**
 * Renders a horizontal row of weight inputs (one per item) that
 * clamp on edit so the group total never exceeds 100. The currently-
 * edited field absorbs the slack; siblings stay put. Shows the
 * unallocated remainder when the sum is below 100.
 */
interface WeightAllocatorProps {
  items: { label: string; value: number; onChange: (next: number) => void }[];
}
const WeightAllocator = ({ items }: WeightAllocatorProps) => {
  const total = items.reduce((acc, it) => acc + (it.value || 0), 0);
  const remaining = 100 - total;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Weights (out of 100):
      </Typography>
      {items.map((it, i) => (
        <TextField
          key={i}
          label={it.label}
          type="number"
          size="small"
          value={it.value}
          onChange={(e) => {
            const raw = Math.max(0, Math.floor(Number(e.target.value) || 0));
            const others = total - (it.value || 0);
            const clamped = Math.min(raw, 100 - others);
            it.onChange(clamped);
          }}
          inputProps={{ min: 0, max: 100, step: 1 }}
          sx={{ width: 100 }}
        />
      ))}
      {remaining !== 0 && (
        <Typography variant="caption" color="text.secondary">
          {remaining > 0 ? `${remaining} unallocated` : "over"}
        </Typography>
      )}
    </Box>
  );
};

/** Per-dimension weight inputs for the Reader rubric. */
const ReaderDimensionWeights = () => {
  const dispatch = useAppDispatch();
  const dimWeights = useAppSelector(selectGradeDimensionWeights);
  const completeness = dimWeights.reader?.completeness ?? 1;
  const categoryAccuracy = dimWeights.reader?.categoryAccuracy ?? 1;
  return (
    <WeightAllocator
      items={[
        {
          label: "Completeness",
          value: completeness,
          onChange: (v) =>
            dispatch(
              setGradeDimensionWeight({
                section: "reader",
                key: "completeness",
                value: v,
              })
            ),
        },
        {
          label: "Category",
          value: categoryAccuracy,
          onChange: (v) =>
            dispatch(
              setGradeDimensionWeight({
                section: "reader",
                key: "categoryAccuracy",
                value: v,
              })
            ),
        },
      ]}
    />
  );
};

/** Per-dimension weight inputs for the Canvas rubric. */
const CanvasDimensionWeights = () => {
  const dispatch = useAppDispatch();
  const dimWeights = useAppSelector(selectGradeDimensionWeights);
  const nodeCoverage = dimWeights.canvas?.nodeCoverage ?? 1;
  const categoryAccuracy = dimWeights.canvas?.categoryAccuracy ?? 1;
  const edgeCoverage = dimWeights.canvas?.edgeCoverage ?? 1;
  return (
    <WeightAllocator
      items={[
        {
          label: "Node",
          value: nodeCoverage,
          onChange: (v) =>
            dispatch(
              setGradeDimensionWeight({
                section: "canvas",
                key: "nodeCoverage",
                value: v,
              })
            ),
        },
        {
          label: "Category",
          value: categoryAccuracy,
          onChange: (v) =>
            dispatch(
              setGradeDimensionWeight({
                section: "canvas",
                key: "categoryAccuracy",
                value: v,
              })
            ),
        },
        {
          label: "Edge",
          value: edgeCoverage,
          onChange: (v) =>
            dispatch(
              setGradeDimensionWeight({
                section: "canvas",
                key: "edgeCoverage",
                value: v,
              })
            ),
        },
      ]}
    />
  );
};

/**
 * Owner-only editor for the editor rubric's criteria list. Each row
 * is a single text field + remove button; an "Add criterion" button
 * appends a blank row.
 */
const EditorRubricCriteriaEditor = () => {
  const dispatch = useAppDispatch();
  const criteria = useAppSelector(selectEditorRubricCriteria);
  const dimWeights = useAppSelector(selectGradeDimensionWeights);
  const criteriaWeights = dimWeights.editorCriteria ?? [];

  const update = (idx: number, value: string) => {
    const next = [...criteria];
    next[idx] = value;
    dispatch(setEditorRubricCriteria(next));
  };
  const remove = (idx: number) => {
    dispatch(setEditorRubricCriteria(criteria.filter((_, i) => i !== idx)));
  };
  const add = () => {
    dispatch(setEditorRubricCriteria([...criteria, ""]));
  };
  const setWeight = (idx: number, value: number) => {
    dispatch(
      setGradeDimensionWeight({ section: "editorCriterion", idx, value })
    );
  };
  const totalWeight = criteria.reduce(
    (acc, _, i) => acc + (criteriaWeights[i] ?? 1),
    0
  );
  const remaining = 100 - totalWeight;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {criteria.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No criteria yet — add some below.
        </Typography>
      )}
      {criteria.map((c, idx) => {
        const w = criteriaWeights[idx] ?? 1;
        return (
          <Box key={idx} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <TextField
              size="small"
              sx={{ flex: 1 }}
              value={c}
              onChange={(e) => update(idx, e.target.value)}
              placeholder="e.g. thesis clearly stated"
            />
            <TextField
              label="weight"
              type="number"
              size="small"
              value={w}
              onChange={(e) => {
                const raw = Math.max(0, Math.floor(Number(e.target.value) || 0));
                const others = totalWeight - w;
                setWeight(idx, Math.min(raw, 100 - others));
              }}
              inputProps={{ min: 0, max: 100, step: 1 }}
              sx={{ width: 88 }}
            />
            <IconButton
              size="small"
              onClick={() => remove(idx)}
              aria-label="remove criterion"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
      {criteria.length > 0 && remaining !== 0 && (
        <Typography variant="caption" color="text.secondary">
          {remaining > 0 ? `${remaining} unallocated weight` : "over 100"}
        </Typography>
      )}
      <Button onClick={add} size="small" sx={{ alignSelf: "flex-start" }}>
        + Add criterion
      </Button>
    </Box>
  );
};

interface RubricSectionProps {
  // Will add more props as needed when extending functionality
}

const RubricSection = ({}: RubricSectionProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectCurrentRubricSettings);
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const workspaceId = useAppSelector(selectWorkspaceId);
  const currentHighlights = useAppSelector(selectCurrentHighlights);
  const rubricLoadGen = useAppSelector(selectCurrentRubricLoadGen);

  // lastSyncedJsonRef holds the criteria JSON we believe the backend
  // already has — either because we just received it via loadRubric or
  // because we just successfully PUT it. The auto-save effect compares
  // the current settings JSON against this ref and only fires a PUT
  // when they diverge, suppressing the echo-save that would otherwise
  // follow each hydration.
  //
  // The sync effect runs first (declared first), so on renders where
  // both loadGen and settings change in the same commit (a load
  // arrival) the ref is updated before the auto-save effect reads it.
  const lastSyncedJsonRef = useRef<string>(JSON.stringify(settings));
  useEffect(() => {
    lastSyncedJsonRef.current = JSON.stringify(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubricLoadGen, selectedReadingId]);

  useEffect(() => {
    if (!selectedReadingId || !workspaceId) return;
    const json = JSON.stringify(settings);
    if (json === lastSyncedJsonRef.current) return;

    const timer = setTimeout(() => {
      updateReadingRubric(workspaceId, selectedReadingId, {
        criteria: json,
      }).then((result) => {
        if (result.success) {
          lastSyncedJsonRef.current = json;
        } else {
          // 403 is expected for contributors; backend gates writes.
          console.error("Rubric auto-save failed:", result.error.message);
        }
      });
    }, RUBRIC_AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [settings, selectedReadingId, workspaceId]);

  const [isSavingExpert, setIsSavingExpert] = useState(false);
  const [expertSaveStatus, setExpertSaveStatus] = useState<
    "idle" | "saved" | "error"
  >("idle");

  // Stage 6.2: parallel state for the canvas expert-save button.
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const currentCanvas = useAppSelector(selectCurrentCanvas);
  const [isSavingExpertCanvas, setIsSavingExpertCanvas] = useState(false);
  const [expertCanvasSaveStatus, setExpertCanvasSaveStatus] = useState<
    "idle" | "saved" | "error"
  >("idle");

  const handleSaveExpertCanvas = async () => {
    if (
      !selectedCanvasId ||
      !workspaceId ||
      !currentCanvas?.canvas_data
    ) {
      return;
    }
    setIsSavingExpertCanvas(true);
    setExpertCanvasSaveStatus("idle");
    const canvasData = currentCanvas.canvas_data;
    const result = await updateCanvasRubric(workspaceId, selectedCanvasId, {
      canvas_data: JSON.stringify(canvasData),
    });
    setIsSavingExpertCanvas(false);
    if (result.success) {
      dispatch(
        setExpertCanvas({
          canvasId: selectedCanvasId,
          canvasData,
        })
      );
      setExpertCanvasSaveStatus("saved");
      setTimeout(() => setExpertCanvasSaveStatus("idle"), 2000);
    } else {
      console.error("Save canvas as expert failed:", result.error.message);
      setExpertCanvasSaveStatus("error");
    }
  };

  const handleSaveExpertHighlights = async () => {
    if (!selectedReadingId || !workspaceId || currentHighlights.length === 0) return;
    setIsSavingExpert(true);
    setExpertSaveStatus("idle");
    const result = await updateReadingRubric(workspaceId, selectedReadingId, {
      coords: JSON.stringify(currentHighlights),
    });
    setIsSavingExpert(false);
    if (result.success) {
      dispatch(
        setExpertHighlights({
          readingId: selectedReadingId,
          highlights: [...currentHighlights],
        })
      );
      setExpertSaveStatus("saved");
      setTimeout(() => setExpertSaveStatus("idle"), 2000);
    } else {
      console.error("Save as expert failed:", result.error.message);
      setExpertSaveStatus("error");
    }
  };

  const completenessEnabled = settings.completeness.enabled;
  const missedAnnotationPenalty = settings.completeness.penalty;
  const categoryMismatchEnabled = settings.categoryMismatch.enabled;
  const categoryMismatchPenalty = settings.categoryMismatch.penalty;
  const matchThreshold = settings.matchThreshold;
  const useRecommended = settings.useRecommendedSettings;

  // Common styles
  const sectionStyle = {
    p: 2,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
  };

  const headerStyle = {
    borderBottom: `1px solid ${theme.palette.divider}`,
    pb: 1,
    mb: 2,
    color: theme.palette.text.primary,
  };

  const settingGroupStyle = {
    mb: 3,
  };

  const settingLabelStyle = {
    display: "flex",
    alignItems: "center",
    mb: 1,
    color: theme.palette.text.secondary,
    "& .MuiIconButton-root": {
      ml: 0.5,
      p: 0.5,
    },
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Rubric Settings
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Reader Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
              Reader Assessment Criteria
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Expert reference snapshot — admin marks up the reading themselves,
                  then clicks here to freeze the current highlights as the rubric's
                  expert set. Grading compares student highlights against this set.
                  Owner-only on the backend; non-owners see a 403 if they click. */}
              <Box sx={settingGroupStyle}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Expert Reference
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Snapshot your current highlights as the expert reference set
                  for this reading. Students are graded against this set.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={handleSaveExpertHighlights}
                  disabled={
                    isSavingExpert ||
                    !selectedReadingId ||
                    currentHighlights.length === 0
                  }
                >
                  {isSavingExpert
                    ? "Saving…"
                    : expertSaveStatus === "saved"
                    ? "Saved ✓"
                    : expertSaveStatus === "error"
                    ? "Save failed — retry"
                    : `Save current highlights as expert (${currentHighlights.length})`}
                </Button>
              </Box>

              <Divider />

              {/* Stage 3.8 follow-up: Recommended/Custom toggle. When
                  Recommended is on, the three knob sections below are
                  replaced by a one-line summary. Toggling on snaps all
                  three knobs back to their defaults — see
                  setRubricUseRecommended in the slice. */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Use recommended defaults
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Turn off to customize the threshold and penalties.
                  </Typography>
                </Box>
                <Switch
                  checked={useRecommended}
                  onChange={(e) =>
                    dispatch(
                      setRubricUseRecommended({
                        useRecommended: e.target.checked,
                      })
                    )
                  }
                  color="secondary"
                />
              </Box>

              {useRecommended ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.03)",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Active rubric:
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    • Match threshold: <strong>{DEFAULT_MATCH_THRESHOLD}%</strong>{" "}
                    overlap required (within the typical 60–80% range)
                    <br />
                    • Completeness penalty:{" "}
                    <strong>{DEFAULT_COMPLETENESS_PENALTY}</strong> pts per missed
                    expert highlight (graduated by partial credit)
                    <br />
                    • Category accuracy penalty:{" "}
                    <strong>{DEFAULT_CATEGORY_MISMATCH_PENALTY}</strong> pts per
                    mismatched matched-pair
                  </Typography>
                </Box>
              ) : (
                <>
              {/* Completeness Section */}
              <Box sx={settingGroupStyle}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Completeness Assessment
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={completenessEnabled}
                        onChange={(e) =>
                          dispatch(
                            setRubricCompleteness({ enabled: e.target.checked })
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Enable"
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box
                  sx={{
                    pl: 1,
                    ...(!completenessEnabled && {
                      opacity: 0.5,
                      pointerEvents: "none",
                    }),
                  }}
                >
                  <Box sx={settingLabelStyle}>
                    <Typography variant="body2">
                      Penalty per missed annotation
                    </Typography>
                    <Tooltip title="Points deducted for each annotation that's missing from the student's work">
                      <IconButton size="small">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Slider
                      value={missedAnnotationPenalty}
                      onChange={(_, value) =>
                        dispatch(
                          setRubricCompleteness({ penalty: value as number })
                        )
                      }
                      min={0}
                      max={20}
                      step={1}
                      sx={{ flexGrow: 1, maxWidth: "calc(100% - 110px)" }}
                      color="secondary"
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value} pts`}
                      marks={[
                        { value: 0, label: "0 pts" },
                        { value: 10, label: "10 pts" },
                        { value: 20, label: "20 pts" },
                      ]}
                    />
                    <TextField
                      value={missedAnnotationPenalty}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 20) {
                          dispatch(setRubricCompleteness({ penalty: value }));
                        }
                      }}
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, max: 20 },
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ width: "100px" }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Accuracy - Category Section */}
              <Box sx={settingGroupStyle}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Category Accuracy Assessment
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={categoryMismatchEnabled}
                        onChange={(e) =>
                          dispatch(
                            setRubricCategoryMismatch({
                              enabled: e.target.checked,
                            })
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Enable"
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box
                  sx={{
                    pl: 1,
                    ...(!categoryMismatchEnabled && {
                      opacity: 0.5,
                      pointerEvents: "none",
                    }),
                  }}
                >
                  <Box sx={settingLabelStyle}>
                    <Typography variant="body2">
                      Penalty per category mismatch
                    </Typography>
                    <Tooltip title="Points deducted when a student selects the wrong category for an annotation">
                      <IconButton size="small">
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Slider
                      value={categoryMismatchPenalty}
                      onChange={(_, value) =>
                        dispatch(
                          setRubricCategoryMismatch({
                            penalty: value as number,
                          })
                        )
                      }
                      min={0}
                      max={10}
                      step={0.5}
                      sx={{ flexGrow: 1, maxWidth: "calc(100% - 110px)" }}
                      color="secondary"
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value} pts`}
                      marks={[
                        { value: 0, label: "0 pts" },
                        { value: 5, label: "5 pts" },
                        { value: 10, label: "10 pts" },
                      ]}
                    />
                    <TextField
                      value={categoryMismatchPenalty}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 10) {
                          dispatch(
                            setRubricCategoryMismatch({ penalty: value })
                          );
                        }
                      }}
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, max: 10, step: 0.5 },
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ width: "100px" }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Stage 3.8: single match-threshold slider. Drives both
                  completeness (graduated credit below threshold) and
                  category-accuracy (only matched pairs are checked).
                  No enable toggle — the threshold is always in effect. */}
              <Box sx={settingGroupStyle}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Match Threshold
                  </Typography>
                  <Tooltip title="Minimum overlap between a student annotation and an expert annotation for the system to count it as 'matched.' Overlap is measured as Intersection-over-Union (IoU): the area both highlights share divided by the area either one covers. Below this threshold, the student gets graduated partial credit; at or above, full credit. Two annotators rarely select identical spans even on the same text — 60–80% is typical.">
                    <IconButton size="small">
                      <HelpOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ pl: 1 }}>
                  <Box sx={settingLabelStyle}>
                    <Typography variant="body2">
                      Required overlap percentage
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Slider
                      value={matchThreshold}
                      onChange={(_, value) =>
                        dispatch(
                          setMatchThreshold({ threshold: value as number })
                        )
                      }
                      min={20}
                      max={MAX_MATCH_THRESHOLD}
                      step={5}
                      sx={{ flexGrow: 1, maxWidth: "calc(100% - 110px)" }}
                      color="secondary"
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                      // Mark the "typical" 60-80% range so users have a
                      // visual cue without needing to read the tooltip.
                      marks={[
                        { value: 25, label: "25%" },
                        {
                          value: TYPICAL_MATCH_THRESHOLD_RANGE[0],
                          label: `${TYPICAL_MATCH_THRESHOLD_RANGE[0]}%`,
                        },
                        {
                          value: TYPICAL_MATCH_THRESHOLD_RANGE[1],
                          label: `${TYPICAL_MATCH_THRESHOLD_RANGE[1]}%`,
                        },
                        { value: MAX_MATCH_THRESHOLD, label: `${MAX_MATCH_THRESHOLD}%` },
                      ]}
                    />
                    <TextField
                      value={matchThreshold}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (
                          !isNaN(value) &&
                          value >= 20 &&
                          value <= MAX_MATCH_THRESHOLD
                        ) {
                          dispatch(setMatchThreshold({ threshold: value }));
                        }
                      }}
                      type="number"
                      InputProps={{
                        inputProps: { min: 20, max: MAX_MATCH_THRESHOLD, step: 5 },
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ width: "100px" }}
                    />
                  </Box>
                  {matchThreshold >= TYPICAL_MATCH_THRESHOLD_RANGE[0] &&
                  matchThreshold <= TYPICAL_MATCH_THRESHOLD_RANGE[1] ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      In the typical range for matching student work to an
                      expert reference.
                    </Typography>
                  ) : matchThreshold > TYPICAL_MATCH_THRESHOLD_RANGE[1] ? (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Above the typical 60–80% range — students will need
                      near-identical selections to score full credit.
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <ReaderDimensionWeights />
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Canvas Section — Stage 6.2: expert canvas snapshot save.
            Settings knobs come in Stage 6.4. */}
        <Box sx={sectionStyle}>
          <Typography variant="h6" sx={headerStyle}>
            Canvas
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Expert Canvas Reference
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Snapshot the current canvas (rectangles, edges, evaluation
            mode) as the expert reference for this canvas. The
            assessCanvas util compares student diagrams against this
            snapshot.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleSaveExpertCanvas}
            disabled={
              isSavingExpertCanvas ||
              !selectedCanvasId ||
              !currentCanvas?.canvas_data ||
              currentCanvas.canvas_data.rectangles.length === 0
            }
          >
            {isSavingExpertCanvas
              ? "Saving…"
              : expertCanvasSaveStatus === "saved"
              ? "Saved ✓"
              : expertCanvasSaveStatus === "error"
              ? "Save failed — retry"
              : `Save current canvas as expert (${
                  currentCanvas?.canvas_data?.rectangles.length ?? 0
                } rects)`}
          </Button>
          <CanvasDimensionWeights />
        </Box>

        {/* Editor Section */}
        <Box sx={sectionStyle}>
          <Typography variant="h6" sx={headerStyle}>
            Editor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bullet-point criteria the contributor's writing is checked
            against. The Grade tab renders one checkbox per item; the
            editor score is the fraction checked.
          </Typography>
          <EditorRubricCriteriaEditor />
        </Box>
      </Box>
    </Box>
  );
};

export default RubricSection;
